import { MenuItem } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton/IconButton";
import Select from "@material-ui/core/Select/Select";
import TextField from "@material-ui/core/TextField/TextField";
import ClearIcon from "@material-ui/icons/Clear";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import React from "react";
import { isMuiListSubheaderElement } from "../../utilities/mui";
import { getNodeText } from "../../utilities/react";

interface IProps {
	variant?: "filled" | "outlined" | "standard";
	value?: string | string[];
	disabled?: boolean;
	clearable?: boolean;
	multiple?: boolean;
	style?: React.CSSProperties;
	autocomplete?: boolean;
	showMissingSelectedOptions?: boolean;
	onClear?: () => void;
	onChange: (value: React.ChangeEvent<HTMLInputElement>) => void;
}

interface ISelectOption {
	value: string;
	label: React.ReactNode;
	labelText: string;
	disabled?: boolean;
	group?: string;
	style?: React.CSSProperties;
}

class ClearableSelect extends React.Component<IProps> {
	render(): JSX.Element {
		if (this.props.autocomplete) {
			return this.renderAutocomplete();
		}

		return this.renderSelect();
	}

	private renderSelect = (): JSX.Element => {
		const { value, variant, disabled, clearable, multiple, style, children, showMissingSelectedOptions, onChange } =
			this.props;
		const displayClear = this.hasValue() ? "" : "none";
		const inputProps = clearable && !!value ? { IconComponent: () => null as any } : undefined;

		return (
			<Select
				disabled={disabled}
				variant={variant}
				value={value}
				multiple={multiple}
				style={style}
				endAdornment={
					clearable ? (
						<IconButton style={{ padding: 0, display: displayClear }} onClick={this.handleClear}>
							<ClearIcon />
						</IconButton>
					) : null
				}
				inputProps={inputProps}
				onChange={(event) => onChange(event as React.ChangeEvent<HTMLInputElement>)}
			>
				{showMissingSelectedOptions && this.renderMissingSelectedOption(this.getOptions())}

				{children}
			</Select>
		);
	};

	private renderAutocomplete = (): JSX.Element => {
		const { disabled, clearable, multiple, style, variant, showMissingSelectedOptions } = this.props;
		const options = this.getOptions();
		const autocompleteOptions = showMissingSelectedOptions ? this.getOptionsWithMissingSelectedOption(options) : options;
		const autocompleteValue = this.getAutocompleteValue(autocompleteOptions);
		const groupBy = autocompleteOptions.some((x) => !!x.group) ? { groupBy: (option: ISelectOption) => option.group ?? "" } : {};

		return (
			<Autocomplete
				{...groupBy}
				disabled={disabled}
				disableClearable
				forcePopupIcon={!clearable || !this.hasValue()}
				fullWidth
				getOptionDisabled={(option: ISelectOption) => !!option.disabled}
				getOptionLabel={(option: ISelectOption) => option.labelText}
				getOptionSelected={(option: ISelectOption, selectedOption: ISelectOption) =>
					option.value === selectedOption.value
				}
				multiple={multiple}
				openOnFocus
				options={options}
				renderInput={(params) => (
					<TextField
						{...params}
						variant={variant}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{clearable && this.hasValue() ? (
										<IconButton
											style={{ padding: 0 }}
											onClick={this.handleClear}
											onMouseDown={this.handleClearMouseDown}
										>
											<ClearIcon />
										</IconButton>
									) : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				renderOption={(option: ISelectOption) => <span style={option.style}>{option.label}</span>}
				renderTags={(selectedOptions: ISelectOption[]) => selectedOptions.map((x) => x.labelText).join(", ")}
				style={style}
				value={autocompleteValue as any}
				onChange={this.handleAutocompleteChange}
			/>
		);
	};

	private renderMissingSelectedOption = (options: ISelectOption[]): React.ReactNode => {
		const legacyOptions = this.getMissingSelectedOption(options);

		return legacyOptions.map((option) => (
			<MenuItem key={`legacy-${option.value}`} value={option.value} disabled>
				{option.label}
			</MenuItem>
		));
	};

	private getMissingSelectedOption = (options: ISelectOption[]): ISelectOption[] => {
		const { value, multiple } = this.props;
		const selectedValues = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

		return selectedValues
			.map((selectedValue) => `${selectedValue}`)
			.filter((selectedValue) => !options.some((option) => option.value === selectedValue))
			.map((selectedValue) => ({
				value: selectedValue,
				label: selectedValue,
				labelText: selectedValue,
				disabled: true,
			}));
	};

	private getOptionsWithMissingSelectedOption = (options: ISelectOption[]): ISelectOption[] => {
		return [...this.getMissingSelectedOption(options), ...options];
	};

	private hasValue = (): boolean => {
		const { value, multiple } = this.props;

		if (multiple) {
			return Array.isArray(value) && value.length > 0;
		}

		return !!value;
	};

	private handleClear = (event?: React.MouseEvent<HTMLButtonElement>): void => {
		if (this.props.autocomplete && event) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (this.props.onClear) {
			this.props.onClear();
		}
	};

	private handleClearMouseDown = (event: React.MouseEvent<HTMLButtonElement>): void => {
		event.preventDefault();
		event.stopPropagation();
	};

	private handleAutocompleteChange = (
		event: React.ChangeEvent<{}>,
		nextValue: ISelectOption | ISelectOption[],
	): void => {
		const { multiple } = this.props;

		const value = multiple
			? ((nextValue as ISelectOption[]) ?? []).map((x) => x.value)
			: ((nextValue as ISelectOption)?.value ?? "");

		this.props.onChange({
			...event,
			target: {
				...event.target,
				value,
			},
		} as React.ChangeEvent<HTMLInputElement>);
	};

	private getAutocompleteValue = (options: ISelectOption[]): ISelectOption | ISelectOption[] | null => {
		const { value, multiple } = this.props;

		if (multiple) {
			const selectedValues = Array.isArray(value) ? value.map((x) => `${x}`) : [];
			return options.filter((x) => selectedValues.includes(x.value));
		}

		return options.find((x) => x.value === `${value}`) ?? null;
	};

	private getOptions = (): ISelectOption[] => {
		const options: ISelectOption[] = [];

		/*
		 * Autocomplete behöver en platt options-lista. Men eftersom vi använder children för att definiera options
		 * så behöver vi gå igenom dessa rekursivt och plocka ut alla options, samt hantera grupper (ListSubheader)
		 * och disabled (för både grupper och options)...
		 */
		this.appendOptions(this.props.children, options);

		return options;
	};

	private appendOptions = (children: React.ReactNode, options: ISelectOption[], group?: string): void => {
		if (Array.isArray(children)) {
			let currentGroup = group;

			children.forEach((child) => {
				if (isMuiListSubheaderElement(child)) {
					currentGroup = getNodeText(child.props.children);
					return;
				}

				this.appendOptions(child, options, currentGroup);
			});

			return;
		}

		React.Children.forEach(children, (child) => {
			if (!React.isValidElement(child)) {
				return;
			}

			const props = child.props as {
				children?: React.ReactNode;
				disabled?: boolean;
				style?: React.CSSProperties;
				value?: string | number;
			};

			if (props.value !== undefined) {
				const labelText = getNodeText(props.children);

				options.push({
					value: `${props.value}`,
					label: props.children,
					labelText,
					disabled: props.disabled,
					group,
					style: props.style,
				});
				return;
			}

			this.appendOptions(props.children, options, group);
		});
	};
}

export default ClearableSelect;
